import { useEffect, useMemo, useRef, useState } from 'react';
import { DashStyleValue } from 'highcharts';
import { readJSON, writeJSON } from '../../../../../hooks/usePersistentState';
import { InputNumber, Select, Space, Tooltip } from 'antd';
import { Button } from '../../../../button';
import { useTheme } from '../../../../../providers/theme';
import {
  CorrelationChartProps,
  ChartSettingsProps
} from '../../../../../@types/data/types';
import { evaluateEquation } from '../../../../../components/chart/convertEquation';
import { ExportOutlined } from '@ant-design/icons';
import { getColors, dropdownStyle } from '../../../../../components/utils/getColors';
import { SelectedFziFunctionsOptions } from '../../../../../@types/projects/types';
import ChartSettings from '../../../../../components/chart/settings';
import { useCorrelation } from '../../../../../providers/Correlation';
// Import from the light context module directly: the provider barrel pulls
// in the API stack, which leaf chart components must not depend on.
import { useAutoCorrelationOptional } from '../../../../../providers/AutoCorrelation/context';
import { useSelected } from '../../../../../providers/selectedContext';
import { Chart } from '../../../../../components/chart/BaseChart';
import { ChartSpec } from '../../../../../components/chart/chartSpec';
import { LazyMount } from '../../../../../components/chart/LazyMount';

interface ChartData {
  values: Array<{
    x: number;
    y: number;
  }>;
  equation: string;
  rSquared: number;
}

const ChartCorrelation: React.FC<CorrelationChartProps> = ({
  received,
  analysisType,
  name,
  xAxis,
  yAxis,
  chartType,
  log,
  field,
  containerWidth,
  height,
  chartColors,
  updateSelectedFziFunctions
}) => {
  const { isDay } = useTheme();
  const colors = getColors(isDay);
  const { localProject, handlePointClick, selectedXAxis } = useCorrelation();
  // Optional: only present in trees wrapped by AutoCorrelationProvider.
  const autoCorrelation = useAutoCorrelationOptional();
  const { selectedInfo } = useSelected();

  // Cache keys for this chart's view config, scoped by project + x-axis + field
  // so overrides don't bleed across projects, axes, or charts.
  const projectUid = selectedInfo.project?.uid ?? 'none';
  const axisKey = `correlation.axis.${projectUid}.${selectedXAxis}.${field}`;
  const fziMeansKey = `correlation.fziMeans.${projectUid}.${selectedXAxis}.${field}`;

  const [selectedOption, setSelectedOption] =
    useState<SelectedFziFunctionsOptions>();
  const [fziMeans, setFziMeans] = useState<number[] | undefined>();

  const chartData = useMemo(() => {
    const optionsList = [
      'linear',
      'exponential',
      'logarithmic',
      'power'
    ] as const;
    const data: {
      [key in SelectedFziFunctionsOptions]: ChartData | null;
    } = {
      linear: null,
      exponential: null,
      logarithmic: null,
      power: null
    };

    const localFziFunctions = selectedXAxis === 'rqi'
      ? localProject?.correlation?.fzi_functions_by_x_axis?.rqi
      : localProject?.correlation?.fzi_functions;
    const persistedFziFunctions = selectedXAxis === 'rqi'
      ? selectedInfo.project?.correlation?.fzi_functions_by_x_axis?.rqi
      : selectedInfo.project?.correlation?.fzi_functions;
    const activeFziFunctions = localFziFunctions ?? persistedFziFunctions;

    if (activeFziFunctions) {
      const tendencyType = activeFziFunctions[field];
      optionsList.forEach(option => {
        const entry = tendencyType?.[option];
        if (!entry || !Array.isArray(entry.x_values_sorted) || !Array.isArray(entry.line_sorted)) {
          data[option] = null;
          return;
        }

        const line = entry.line_sorted;
        const xsorted = entry.x_values_sorted;
        const equation = entry.equation;
        const rSquared = entry.r_squared;
        const values = xsorted.map((x, index) => ({
          x,
          y: line[index]
        }));

        data[option] = {
          equation,
          rSquared,
          values
        };
      });
    }

    return data;
  }, [localProject?.correlation?.fzi_functions, localProject?.correlation?.fzi_functions_by_x_axis?.rqi, selectedInfo.project?.correlation?.fzi_functions, selectedInfo.project?.correlation?.fzi_functions_by_x_axis?.rqi, selectedXAxis, field]);

  const selectedChartData = useMemo(() => {
    if (!selectedOption) return null
    return chartData[selectedOption]
  }, [chartData, selectedOption])

  const limitsFromReceived = useMemo(() => {
    const xVals = received?.map(p => p.x) || [];
    const yVals = received?.map(p => p.y) || [];
    return {
      minX: xVals.length > 0 ? Math.min(...xVals) : 0,
      maxX: xVals.length > 0 ? Math.max(...xVals) : 100,
      minY: yVals.length > 0 ? Math.min(...yVals) : 0,
      maxY: yVals.length > 0 ? Math.max(...yVals) : 100
    };
  }, [received]);

  const { primaryPoints, secondaryPoints } = useMemo(() => {
    const primary = received.filter(
      point => point.comparisonRole !== 'secondary'
    );
    const secondary = received.filter(
      point => point.comparisonRole === 'secondary'
    );

    return {
      primaryPoints: primary,
      secondaryPoints: secondary
    };
  }, [received]);

  const primarySeriesName = useMemo(() => {
    const source = primaryPoints[0]?.source;
    if (source === 'refinement') return 'Refinement';
    if (source === 'lab') return 'Lab';
    return 'Measured Data';
  }, [primaryPoints]);

  const secondarySeriesName = useMemo(() => {
    const source = secondaryPoints[0]?.source;
    if (source === 'lab') return 'Lab';
    if (source === 'refinement') return 'Refinement';
    return 'Secondary';
  }, [secondaryPoints]);

  const limitsFromCalculated = useMemo(() => {
    const xVals = selectedChartData?.values?.map(p => p.x) || [];
    const yVals = selectedChartData?.values?.map(p => p.y) || [];
    return {
      minX: xVals.length > 0 ? Math.min(...xVals) : undefined,
      maxX: xVals.length > 0 ? Math.max(...xVals) : undefined,
      minY: yVals.length > 0 ? Math.min(...yVals) : undefined,
      maxY: yVals.length > 0 ? Math.max(...yVals) : undefined
    };
  }, [selectedChartData]);

  const autoDefaults = useMemo(() => ({
    xmin: Math.min(
      limitsFromReceived.minX ?? Infinity,
      limitsFromCalculated.minX ?? Infinity
    ),
    xmax: Math.max(
      limitsFromReceived.maxX ?? -Infinity,
      limitsFromCalculated.maxX ?? -Infinity
    ),
    ymin: Math.min(
      limitsFromReceived.minY ?? Infinity,
      limitsFromCalculated.minY ?? Infinity
    ),
    ymax: Math.max(
      limitsFromReceived.maxY ?? -Infinity,
      limitsFromCalculated.maxY ?? -Infinity
    ),
  }), [limitsFromCalculated, limitsFromReceived]);

  // Cached axis limits (null when the user never overrode them → follow auto).
  const [chartSettings, setChartSettings] = useState<ChartSettingsProps>(() => {
    const stored = readJSON<ChartSettingsProps | null>(axisKey, null);
    return (
      stored ?? {
        xmin: autoDefaults.xmin,
        xmax: autoDefaults.xmax,
        ymin: autoDefaults.ymin,
        ymax: autoDefaults.ymax
      }
    );
  });

  const [userHasSetLimits, setUserHasSetLimits] = useState(
    () => readJSON<ChartSettingsProps | null>(axisKey, null) != null
  );

  // Reload the cached override when the scope (project / x-axis) changes. Skips
  // the initial run (already handled by the useState initializers above).
  const axisKeyRef = useRef(axisKey);
  useEffect(() => {
    if (axisKeyRef.current === axisKey) return;
    axisKeyRef.current = axisKey;
    const stored = readJSON<ChartSettingsProps | null>(axisKey, null);
    if (stored) {
      setChartSettings(stored);
      setUserHasSetLimits(true);
    } else {
      setUserHasSetLimits(false);
    }
  }, [axisKey]);

  useEffect(() => {
    if (!userHasSetLimits) {
      setChartSettings({
        xmin: autoDefaults.xmin,
        xmax: autoDefaults.xmax,
        ymin: autoDefaults.ymin,
        ymax: autoDefaults.ymax
      });
    }
  }, [autoDefaults, userHasSetLimits]);

  const handleSetChartSettingsFromModal = (newSettings: ChartSettingsProps) => {
    setChartSettings(newSettings);
    setUserHasSetLimits(true);
    writeJSON(axisKey, newSettings);
  };

  function getFunctionTypesOptions() {
    return ['linear', 'power', 'logarithmic', 'exponential'].map(optionType => {
      const label = optionType.charAt(0).toUpperCase() + optionType.slice(1);
      const persistedSlot = selectedXAxis === 'rqi'
        ? selectedInfo.project?.correlation?.fzi_functions_by_x_axis?.rqi
        : selectedInfo.project?.correlation?.fzi_functions;
      const selected = persistedSlot?.[field]?.selected;
      return (
        <Select.Option key={optionType} value={optionType}>
          {selected === optionType ? `${label} *` : label}
        </Select.Option>
      );
    });
  }

  const handleInputChange = (index, newValue) => {
    const updatedFziMeans = fziMeans ? [...fziMeans] : [];
    updatedFziMeans[index] = newValue || 0;
    setFziMeans(updatedFziMeans);
    writeJSON(fziMeansKey, updatedFziMeans);
  };

  const handleOptionChange = (value: SelectedFziFunctionsOptions) => {
    setSelectedOption(value);
    updateSelectedFziFunctions?.(field, value);
  };

  // Small "Auto" tag when the currently selected function is the one the
  // applied Auto Correlation report picked for this field. Skipped whenever
  // the provider/report is absent or the user changed the function since.
  const autoReport = autoCorrelation?.report;
  const isAutoSelection = Boolean(
    autoReport?.applied &&
      selectedOption &&
      autoReport?.applied_selection?.[field] === selectedOption
  );
  const autoSelection = autoReport?.applied_selection?.[field];
  const isManualOverride = Boolean(
    autoReport?.applied &&
      autoSelection &&
      selectedOption &&
      autoSelection !== selectedOption
  );

  const handleResetToAuto = () => {
    if (!autoSelection) return;
    setSelectedOption(autoSelection);
    updateSelectedFziFunctions?.(field, autoSelection);
  };

  useEffect(() => {
    const persistedSlot = selectedXAxis === 'rqi'
      ? selectedInfo.project?.correlation?.fzi_functions_by_x_axis?.rqi
      : selectedInfo.project?.correlation?.fzi_functions;
    const selected = persistedSlot?.[field]?.selected;
    setSelectedOption(selected ?? undefined);
  }, [selectedInfo.project?.correlation?.fzi_functions, selectedInfo.project?.correlation?.fzi_functions_by_x_axis?.rqi, selectedXAxis, field]);

  const hexToRgba = (hex: string, alpha: number) => {
    let r = 0,
      g = 0,
      b = 0;

    if (hex.length === 4) {
      r = parseInt(hex[1] + hex[1], 16);
      g = parseInt(hex[2] + hex[2], 16);
      b = parseInt(hex[3] + hex[3], 16);
    } else if (hex.length === 7) {
      r = parseInt(hex[1] + hex[2], 16);
      g = parseInt(hex[3] + hex[4], 16);
      b = parseInt(hex[5] + hex[6], 16);
    }

    return `rgba(${r},${g},${b},${alpha})`;
  };

  const plotZones = useMemo(
    () =>
      localProject?.correlation?.zones?.map((zone, idx, arr) => ({
        from: zone.start,
        to: idx === arr.length - 1 ? Infinity : zone.end,
        color: hexToRgba(zone.color || '#fff', 0.35)
      })) || [],
    [localProject?.correlation?.zones]
  );

  const spec: ChartSpec = useMemo(
    () => ({
      title: name,
      height,
      legend: {
        enabled: secondaryPoints.length > 0,
        fontSize: '11px',
        symbolWidth: 16,
        symbolHeight: 8
      },
      xAxis: {
        title: xAxis,
        type: log ? 'logarithmic' : 'linear',
        plotBands: plotZones,
        min: log
          ? chartSettings.xmin !== undefined && chartSettings.xmin > 0
            ? chartSettings.xmin
            : 0.001
          : chartSettings.xmin ?? autoDefaults.xmin,
        max: log
          ? chartSettings.xmax !== undefined && chartSettings.xmax > 0
            ? chartSettings.xmax
            : 1000
          : chartSettings.xmax ?? autoDefaults.xmax
      },
      yAxis: {
        title: yAxis,
        type: log ? 'logarithmic' : 'linear',
        min: log
          ? chartSettings.ymin !== undefined && chartSettings.ymin > 0
            ? chartSettings.ymin
            : 0.1
          : chartSettings.ymin ?? autoDefaults.ymin,
        max: log
          ? chartSettings.ymax !== undefined && chartSettings.ymax > 0
            ? chartSettings.ymax
            : 10000
          : chartSettings.ymax ?? autoDefaults.ymax
      },
      onPointClick: event => handlePointClick(event.point.name, analysisType),
      series: [
        {
          name: 'Calculated Data',
          // Fresh point objects (not the memoized per-function array) so
          // Highcharts' in-place `chart.update` actually repaints the line when
          // the selected fit function changes. Reusing the memoized reference
          // let Highcharts skip the redraw, leaving the line on the previously
          // previewed function until an unrelated recompute regenerated it.
          type: chartType === 'line' ? 'line' : 'scatter',
          data: selectedChartData?.values?.map(p => ({ x: p.x, y: p.y })) || [],
          color: chartColors?.line || '#72E2D8',
          marker: {
            radius: chartType === 'line' ? 0 : 5,
            enabled: chartType !== 'line'
          },
          enableMouseTracking: false,
          states: {
            hover: {
              enabled: false
            }
          },
          lineWidth: 1.7,
          zIndex: 2
        },
        {
          name: secondaryPoints.length > 0 ? primarySeriesName : 'Measured Data',
          type: 'scatter',
          data: primaryPoints,
          dataLabels: {
            enabled: false,
            format: '{point.name}'
          },
          tooltip: {
            headerFormat: '',
            pointFormat:
              '<b>{point.name}</b><br/><b>X</b>: {point.x} <br/> <b>Y</b> {point.y}'
          },
          zIndex: 1
        },
        ...(secondaryPoints.length > 0
          ? [
              {
                name: secondarySeriesName,
                type: 'scatter' as const,
                data: secondaryPoints,
                color: 'rgba(120,120,120,0.32)',
                marker: {
                  symbol: 'diamond',
                  radius: 3
                },
                dashStyle: 'Dash' as DashStyleValue,
                dataLabels: {
                  enabled: false
                },
                tooltip: {
                  enabled: false
                },
                zIndex: 0
              }
            ]
          : [])
      ],
    }),
    [
      primaryPoints,
      secondaryPoints,
      analysisType,
      selectedChartData?.values,
      primarySeriesName,
      secondarySeriesName,
      height,
      log,
      name,
      plotZones,
      xAxis,
      yAxis,
      handlePointClick,
      chartType,
      colors.text,
      chartColors.line,
      autoDefaults,
      chartSettings
    ]
  );

  useEffect(() => {
    const derived = localProject?.correlation?.zones?.map(zone =>
      zone.mean != null && !Number.isNaN(zone.mean)
        ? zone.mean
        : (zone.start + zone.end) / 2
    );
    if (!derived) {
      setFziMeans(undefined);
      return;
    }
    // Use the cached edits only when they still line up with the current zones;
    // otherwise (zones added/removed) fall back to the derived means.
    const stored = readJSON<number[] | null>(fziMeansKey, null);
    setFziMeans(
      Array.isArray(stored) && stored.length === derived.length
        ? stored
        : derived
    );
  }, [localProject?.correlation?.zones, fziMeansKey]);

  const exportData = () => {
    if (localProject?.correlation?.zones && selectedChartData?.equation) {
      const headers = ['FZI Mean', 'Equation', 'Result (y)'];
      const rows = localProject.correlation.zones.map((zone, index) => {
        const fziMeanValue = fziMeans
          ? fziMeans[index]
          : zone.mean != null && !Number.isNaN(zone.mean)
          ? zone.mean
          : (zone.start + zone.end) / 2;
        const fziMean = fziMeanValue.toFixed(4);
        const result = evaluateEquation(
          selectedChartData.equation,
          Number(fziMeanValue)
        );
        return [fziMean, selectedChartData.equation, result];
      });
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.join(','))
      ].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `${name || 'correlation-chart'}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <LazyMount style={{ width: containerWidth, minHeight: 360 }}>
    <Space
      direction="vertical"
      style={{
        backgroundColor: 'transparent',
        marginTop: '1rem',
        border: `1px solid ${colors.border}`,
        borderRadius: '16px',
        width: '100%',
        transition: 'width 0.5s'
      }}
    >
      <Space style={{ display: 'block' }}>
        <div style={{ borderBottom: `1px solid ${colors.border}` }}>
          <div
            style={{
              display: 'flex',
              padding: '0.75rem',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '10px'
            }}
          >
            <Space>
              <Select
                value={selectedOption}
                onChange={handleOptionChange}
                dropdownStyle={dropdownStyle(isDay)}
                placeholder="Correlation Analysis"
                data-testid="correlation-analysis"
                style={{ minWidth: '180px' }}
              >
                {getFunctionTypesOptions()}
              </Select>
              {isAutoSelection ? (
                <Tooltip title="Selected by the applied Auto Correlation report">
                  <span
                    data-testid="auto-selection-badge"
                    style={{
                      padding: '0 0.4rem',
                      borderRadius: '4px',
                      fontSize: '0.65rem',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                      background: 'rgba(114, 226, 216, 0.18)',
                      color: '#12a594',
                      border: '1px solid rgba(18, 165, 148, 0.4)'
                    }}
                  >
                    Auto
                  </span>
                </Tooltip>
              ) : null}
              {isManualOverride ? (
                <>
                  <Tooltip title="This function differs from the last applied Auto Correlation selection">
                    <span
                      data-testid="manual-override-badge"
                      style={{
                        padding: '0 0.4rem',
                        borderRadius: '4px',
                        fontSize: '0.65rem',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                        background: 'rgba(250, 173, 20, 0.14)',
                        color: '#ad6800',
                        border: '1px solid rgba(250, 173, 20, 0.5)'
                      }}
                    >
                      Manual override
                    </span>
                  </Tooltip>
                  <Button variant="secondary" onClick={handleResetToAuto}>
                    Reset to Auto
                  </Button>
                </>
              ) : null}
              <ChartSettings
                chartSettings={chartSettings}
                setChartSettings={handleSetChartSettingsFromModal}
                autoDefaults={autoDefaults}
              />
            </Space>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexGrow: 1,
                textAlign: 'center'
              }}
            >
              {selectedChartData?.equation ? (
                <span style={{ marginRight: '1rem' }}>
                  {selectedChartData.equation}
                </span>
              ) : null}
              {selectedChartData?.rSquared ? (
                <span>r²: {selectedChartData.rSquared.toFixed(5)}</span>
              ) : null}
            </div>
          </div>
        </div>
        <div style={{ padding: '0.75rem' }}>
          <Chart spec={spec} />
        </div>
      </Space>
      {selectedChartData?.equation && localProject?.correlation?.zones?.length ? (
        <div style={{ padding: '0.75rem' }}>
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              marginTop: '0.5rem'
            }}
          >
            <thead>
              <tr style={{ borderBottom: `1px solid ${colors.text}` }}>
                <th style={{ padding: '0.5rem', textAlign: 'center', width: '25%' }}>FZI Mean</th>
                <th style={{ padding: '0.5rem', textAlign: 'center', width: '35%' }}>Equation</th>
                <th style={{ padding: '0.5rem', textAlign: 'center', width: '25%' }}>Result (y)</th>
                <th style={{ padding: '0.5rem', textAlign: 'center', width: '15%' }}></th>
              </tr>
            </thead>
            <tbody>
              {localProject.correlation.zones.map((zone, index) => {
                const fallbackMean =
                  zone.mean != null && !Number.isNaN(zone.mean)
                    ? zone.mean
                    : (zone.start + zone.end) / 2;
                const fziMeanValue = fziMeans ? fziMeans[index] : fallbackMean;
                return (
                  <tr key={index}>
                    <td style={{ padding: '0.5rem', textAlign: 'center', width: '25%' }}>
                      <InputNumber
                        value={fziMeanValue}
                        onChange={value => handleInputChange(index, value)}
                        style={{ width: '7rem' }}
                        step={0.01}
                        precision={4}
                      />
                    </td>
                    <td style={{ padding: '0.5rem', textAlign: 'center', width: '35%' }}>
                      {selectedChartData.equation}
                    </td>
                    <td style={{ padding: '0.5rem', textAlign: 'center', width: '25%' }}>
                      {evaluateEquation(selectedChartData.equation, fziMeanValue)}
                    </td>
                    <td style={{ padding: '0.5rem', textAlign: 'center', width: '15%' }}>
                      {index === 0 ? (
                        <Tooltip title="Export table data">
                          <Button variant="ghost" size="sm" startIcon={<ExportOutlined />} onClick={exportData} />
                        </Tooltip>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
    </Space>
    </LazyMount>
  );
};

export default ChartCorrelation;
